import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  Button as MuiButton,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  ListItemText as MuiListItemText,
  List as MuiList,
  ListItem as MuiListItem,
  Typography as MuiTypography,
  Divider as MuiDivider,
  Grid as MuiGrid,
} from "@mui/material";
import { ErrorOutline as MuiErrorOutlineIcon } from "@mui/icons-material";
import { Theme, useAlerts } from "common";
import { format } from "date-fns";
import {
  getOutreachFailureSummary,
  clearOutreachFailureSummary,
  useStudies,
} from "app/services/studiesService";
import { renderContentBySplitingOnFullStop } from "../utils";
import { LOADER_TYPES } from "../constants";
import { Loader } from "./Loader";

const OutreachFailureSummary = ({
  onClose,
  open,
  isDisplayConductOutreachButton,
  studyId,
  outreachRefresh,
  setOutreachRefresh,
  navigate,
}) => {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [outreachFailureSummaryReport, setOutreachFailureSummaryReport] =
    useState([]);
  const [irbNumber, setIRBNumber] = useState("");

  const initialLoading = {
    outreachStudyFailureSummary: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  useEffect(() => {
    getOutreachFailureSummary(
      studyId,
      setIRBNumber,
      setOutreachFailureSummaryReport,
      setAlert,
      setLoader
    ); // eslint-disable-next-line
  }, [studyId]);

  const { studies, setStudies } = useStudies();

  const handleClearAlert = () => {
    setIRBNumber("");
    clearOutreachFailureSummary(
      studyId,
      setOutreachFailureSummaryReport,
      setAlert,
      studies,
      setStudies,
      setOutreachRefresh,
      outreachRefresh,
      navigate
    );
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="xs">
      <MuiDialogTitle id="risk-condition-dialog-title" disableTypography>
        <MuiErrorOutlineIcon
          color="error"
          sx={{
            verticalAlign: "sub",
            marginRight: "8px",
          }}
          fontSize="small"
        />
        {t(`OutreachFailureSummary.title`)}
      </MuiDialogTitle>
      <MuiDivider
        sx={{ borderBottom: `1px solid ${Theme.palette.common.black}` }}
      />

      {isLoading[LOADER_TYPES.OUTREACH_STUDY_FAILURE_SUMMARY] ? (
        <MuiGrid container justifyContent="center" alignItems="center">
          <Loader />
        </MuiGrid>
      ) : (
        <MuiDialogContent sx={{ padding: 0 }}>
          <MuiList>
            <MuiListItem alignItems="center" dense={true}>
              <MuiListItemText
                disableTypography
                primary={
                  <MuiTypography variant="subtitle1">
                    {irbNumber && t(`OutreachFailureSummary.irb`)} {irbNumber}
                  </MuiTypography>
                }
              />
            </MuiListItem>
            {outreachFailureSummaryReport.length > 0 ? (
              outreachFailureSummaryReport.map((row, index) => (
                <>
                  <MuiListItem key={index} alignItems="center" dense={true}>
                    <MuiListItemText
                      disableTypography
                      primary={
                        <MuiTypography variant="subtitle1">
                          {(row.scheduleType === "ONE_TIME_ONLY" &&
                            t(`OutreachFailureSummary.outreach`)) ||
                            t(`OutreachFailureSummary.recurringSchedule`)}{" "}
                          {row.waveName}
                        </MuiTypography>
                      }
                      secondary={
                        <MuiTypography variant="caption" color="textSecondary">
                          {format(
                            new Date(row.issueDate),
                            "yyyy-MM-dd hh:mm a"
                          )}{" "}
                          {row.failureReason
                            ? renderContentBySplitingOnFullStop(
                                row.failureReason
                              )
                            : ""}
                        </MuiTypography>
                      }
                    />
                  </MuiListItem>
                  <MuiDivider />
                </>
              ))
            ) : (
              <MuiTypography align="center" variant="subtitle1">
                {t(`OutreachFailureSummary.noSummaryAvailable`)}
              </MuiTypography>
            )}
          </MuiList>
        </MuiDialogContent>
      )}

      <MuiDialogActions
        sx={
          (isDisplayConductOutreachButton && {
            justifyContent: "space-between",
          }) ||
          ""
        }
      >
        <MuiButton
          onClick={() => handleClearAlert()}
          disabled={!outreachFailureSummaryReport.length}
          color="primary"
        >
          {t(`OutreachFailureSummary.clearAlertLabel`)}
        </MuiButton>
        {isDisplayConductOutreachButton && (
          <Link
            to={{
              pathname: `/outreach/${studyId}/conduct`,
            }}
          >
            <MuiButton color="primary">
              {t(`OutreachFailureSummary.goToConductOutreachLabel`)}
            </MuiButton>
          </Link>
        )}
        <MuiButton onClick={onClose} color="primary">
          {t(`Close`)}
        </MuiButton>
      </MuiDialogActions>
    </MuiDialog>
  );
};

export default OutreachFailureSummary;
